import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, DropdownMenu, DropdownItem, Popup } from 'semantic-ui-react';
import { ResidentLocationParts } from '../../../types/resident-location';
import { useNeighborsNav } from '../../../neighbors/contexts/NeighborsNavContext';
import TruncatedText from '../../../neighbors/components/TruncatedText';
import { CloseIcon, HamburgerIcon, PinFilledIcon, PinOutlineIcon } from '../../../neighbors/components/icons';
import { neighborsLogoPath } from '../../../utils/logoPaths';
import { getResidentDisplayFirstName, getResidentDisplayImages, getResidentDisplayLastName } from '../../../neighbors/utils/residentIdentity';

interface NeighborsAppHeaderProps {
    residentFullName: string;
    location?: ResidentLocationParts | null;
    activeResident?: {
        Image?: string;
        ExternalResidentImage?: string;
        FirstName?: string;
        LastName?: string;
        SocialProfile?: {
            Image?: string;
            FirstName?: string;
            LastName?: string;
        };
    };
    isMailIn?: boolean;
    isMailInLoading?: boolean;
    isMailInUnavailable?: boolean;
    onAccountSettingChange: (value: string) => void;
    /** The slide-out drawer (`NeighborsSidenav`) only mounts for managers — showing
     *  the hamburger to anyone else would be a dead control with nothing to open. */
    hasMobileNav?: boolean;
}

const APP_HEADER_MENU_OPTIONS = [
    { key: 'profile', text: 'Profile', value: 'profile' },
    { key: 'logout', text: 'Log Out', value: 'logout' },
    { key: 'delete-account', text: 'Delete Account', value: 'delete account' },
] as const;

const NeighborsAppHeader = ({
    residentFullName,
    location,
    activeResident,
    isMailIn,
    isMailInLoading,
    isMailInUnavailable,
    onAccountSettingChange,
    hasMobileNav = false,
}: NeighborsAppHeaderProps) => {
    const { isMobileNavOpen, toggleMobileNav } = useNeighborsNav();

    // Every usable photo in order; if one fails to load, the next is tried before falling back to initials.
    const avatarImages = getResidentDisplayImages(activeResident);
    const avatarImagesKey = avatarImages.join('|');
    const avatarFirstName = getResidentDisplayFirstName(activeResident);
    const avatarLastName = getResidentDisplayLastName(activeResident);
    const [failedAvatarCount, setFailedAvatarCount] = useState(0);
    useEffect(() => setFailedAvatarCount(0), [avatarImagesKey]);
    const avatarImage = avatarImages[failedAvatarCount];
    const avatarContent = avatarImage ? (
        <img
            src={avatarImage}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setFailedAvatarCount((count) => count + 1)}
        />
    ) : (
        <span className="nh-avatar-initials text-nbp-14 font-semibold text-nb-navy md:text-nbp-16">
            {(avatarFirstName?.charAt(0) || '') + (avatarLastName?.charAt(0) || '') || '👤'}
        </span>
    );

    return (
        <div className="neighbors-app-header flex h-nbp-64 items-center justify-end gap-nbp-12 px-nbp-16 font-nb-body leading-normal md:h-nbp-80 md:gap-nbp-20 md:px-nbp-24 lg:px-nbp-40">
            {/* Phone-only: opens the slide-out nav that replaces the rail below 768px.
                Only rendered when a `NeighborsSidenav` is actually mounted for this
                user — otherwise there's nothing for it to open. */}
            {hasMobileNav && (
                <button
                    type="button"
                    onClick={toggleMobileNav}
                    aria-label={isMobileNavOpen ? 'Close navigation' : 'Open navigation'}
                    aria-expanded={isMobileNavOpen}
                    className="flex h-nbp-44 w-nbp-44 flex-shrink-0 cursor-pointer items-center justify-center rounded-nbp-12 border-0 bg-transparent p-0 text-nb-navy transition-all duration-150 hover:bg-nb-navy-08 md:hidden"
                >
                    {isMobileNavOpen ? <CloseIcon size={24} /> : <HamburgerIcon size={24} />}
                </button>
            )}

            {/* Phone-only: same brand mark as the sidenav, linking home. Hidden from md up
                because the rail already shows it. */}
            <Link
                to="/home"
                aria-label="Speak2 Neighbors home"
                className="flex h-nbp-44 flex-shrink-0 items-center no-underline md:hidden"
            >
                <img
                    src={`${process.env.PUBLIC_URL}${neighborsLogoPath}`}
                    alt="Speak2 Neighbors"
                    className="block h-nbp-40 w-auto max-w-full object-contain"
                />
            </Link>

            <div className="nh-header-right relative z-10 flex min-w-0 flex-grow items-center justify-end gap-nbp-12 md:gap-nbp-20">
                <Popup
                    content={
                        isMailInLoading
                            ? 'Loading mail status...'
                            : isMailInUnavailable
                                ? 'Unable to load mail status.'
                                : isMailIn
                                    ? 'The mail has arrived.'
                                    : "The mail hasn't arrived yet today."
                    }
                    trigger={
                        <span
                            className="nh-msg-btn relative order-2 flex h-nbp-40 w-nbp-40 flex-shrink-0 transform cursor-pointer items-center justify-center rounded-full border-hair border-solid border-nb-mail-border bg-nb-mail-bg text-nbp-18 transition-all duration-200 hover:bg-nb-mail-bg-hover hover:scale-105 md:order-1 md:h-nbp-48 md:w-nbp-48 md:text-nbp-20"
                            role="img"
                            aria-label="Mail status"
                        >
                            ✉️
                            {isMailIn && (
                                <span className="nh-msg-badge absolute -top-nbp-4 -right-nbp-4 flex h-nbp-20 w-nbp-20 items-center justify-center rounded-full bg-nb-orange text-nbp-11 font-bold text-white">
                                    1
                                </span>
                            )}
                        </span>
                    }
                    on="hover"
                    size="small"
                    position="bottom center"
                />
                <div className="nh-user-info order-1 min-w-0 flex-grow text-left md:order-2 md:flex-grow-0 md:text-right">
                    <TruncatedText className="font-nb-heading text-nbp-15 font-bold text-nb-navy md:text-nbp-18">
                        {residentFullName}
                    </TruncatedText>
                    {location?.shortLocation && (
                        <div className="nh-user-location-wrap mt-nbp-2 flex items-center text-nb-text-light md:justify-end" style={{ gap: 5 }}>
                            <span
                                aria-hidden="true"
                                className="inline-flex flex-shrink-0"
                                style={location.isOffPremise ? undefined : { color: '#3d8a5e' }}
                            >
                                {location.isOffPremise
                                    ? <PinOutlineIcon size={13} />
                                    : <PinFilledIcon size={13} />}
                            </span>
                            <TruncatedText
                                className="nh-user-location text-nbp-11 text-nb-text-light md:text-nbp-13"
                                popupContent={location.fullLocation || undefined}
                                forcePopup={!!location.fullLocation}
                                on={['hover', 'click']}
                            >
                                {location.shortLocation}
                            </TruncatedText>
                        </div>
                    )}
                </div>
                <Dropdown
                    icon={null}
                    className="order-3 flex-shrink-0"
                    trigger={
                        <div className="nh-avatar flex h-nbp-40 w-nbp-40 cursor-pointer items-center justify-center overflow-hidden rounded-full border-3 border-solid border-white shadow-nb-avatar md:h-nbp-50 md:w-nbp-50">
                            {avatarContent}
                        </div>
                    }
                    floating
                >
                    <DropdownMenu direction="left">
                        {APP_HEADER_MENU_OPTIONS.map((option) => (
                            <DropdownItem
                                key={option.key}
                                onClick={() => onAccountSettingChange(option.value)}
                                style={option.key === 'delete-account' ? { color: '#c0392b' } : undefined}
                            >
                                {option.text}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            </div>
        </div>
    );
};

export default NeighborsAppHeader;
